import {Alpine as IAlpine} from 'alpinejs';
import HTMLPlayer from './HTMLPlayer';
import VimeoPlayer from './VimeoPlayer';
import YoutubePlayer from './YoutubePlayer';

enum ALLOWED_MEDIA_TYPES {
  'mp4' = 'mp4',
  'vimeo' = 'vimeo',
  'youtube' = 'youtube',
  'webm' = 'webm'
}

const __getPlayer = function (el: HTMLElement, key: ALLOWED_MEDIA_TYPES) {
  switch (key) {
  case 'youtube':
    return new YoutubePlayer(el);
  case 'vimeo':
    return new VimeoPlayer(el);
  default:
    return new HTMLPlayer(el);
  }
};

// Binds a video player to `el` for the given media type. Extracted from the `video-player` Alpine
// directive so it can also be called manually for markup that was not present yet when Alpine first
// processed the page (e.g. a YouTube embed decoded later by EXT:a11y_consent, see the
// `consent-video` plugin) - Alpine directives only run once per element and do not re-trigger for
// content injected into an already-processed element afterwards.
export function bindVideoPlayer(el: HTMLElement, expression: string): void {
  const video = el.querySelector('.media-item');
  if (!video) return;
  if (!(expression in ALLOWED_MEDIA_TYPES)) return;
  const playerKey = expression ? expression.toLowerCase() : '';
  const player = __getPlayer(el, playerKey as ALLOWED_MEDIA_TYPES);

  el.addEventListener('videoPlayer.ready', () => {
    const playButton = el.querySelector('button');
    el.addEventListener('videoPlayer.play', () => {
      player.play();
      el.classList.add('is-playing');
      el.classList.remove('is-paused');
    });
    el.addEventListener('videoPlayer.stop', () => {
      player.stop();
      el.classList.add('is-paused');
      el.classList.remove('is-playing');
    });
    if (playButton) {
      playButton.addEventListener('click', () => {
        if (el.classList.contains('is-playing')) {
          el.dispatchEvent(player.events.stop);
        } else {
          el.dispatchEvent(player.events.play);
        }
      });
    }
  });

  if (video.getAttribute('src') || video.querySelector('[src]')) {
    el.dispatchEvent(player.events.loadScript);
  } else {
    new MutationObserver(() => {
      el.dispatchEvent(player.events.loadScript);
    }).observe(video, {
      attributeFilter: ['src']
    });
  }
}

export default function (Alpine: IAlpine) {
  Alpine.directive('video-player', (el, {expression}) => {
    bindVideoPlayer(el, expression);
  });
}
